import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';
import { BillingInfoFieldName, TransactionCategory } from '@shipstation/financial-services-ui-components';
import type { BillingInfoData } from '@shipstation/financial-services-ui-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';

import { Button } from 'ui-components/components/button';
import { FlexColumn } from 'ui-components/components/flex/column';
import { Gap } from 'ui-components/components/flex/gap';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { GuidService } from 'ui-components/services/guid-service';
import { NotificationService } from 'ui-components/services/notification-service';

import { ModalPortal } from '../modal-portal';
import { ErrorBoundary } from '../error-boundary';
import { US } from '../../constants/country-codes';
import FsuiContextProvider from '../../financial-services-ui/fsui-context-provider';
import { FinancialServicesUIProviderWrapper } from '../../financial-services-ui/financial-services-ui-provider-wrapper';
import { usePaymentAndSubscriptionMegaContext } from '../../pages/settings/payment-and-subscription/context';
import { getFreightProvidersTabPath } from '../../routes/constants';
import Global from '../../store/global-selectors';
import {
  CarrierConnectionErrorSource,
  CarrierConnectionInteractionAction,
  CarrierConnectionInteractionSection,
  CarrierConnectionPaymentMethodSource,
  Workflows,
} from '../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../tracking/use-workflow-tracking';
import { useYupFormValidation } from '../../validation/use-yup-form-validation';

import { AddPaymentMethodView } from './components/add-payment-method-view';
import { EditAccountInfoView } from './components/edit-account-info-view';
import { EditBillingAddressView } from './components/edit-billing-address-view';
import { WwexSummaryView } from './components/wwex-summary-view';
import { openWwexAccount } from './wwex-registration.api';
import type { Address, WwexFormData, WwexModalView } from './types';
import type { ValidationError } from '../../validation/validation-error';
import { messages } from './messages';
import { buildWwexFormSchema } from './wwex-form-validation-schema';
import style from './wwex-registration-modal.module.less';

const buildInitialFormData = (contactFullName: string, companyName: string, email: string, phoneNumber: string): Partial<WwexFormData> => ({
  contactFullName,
  companyName,
  email,
  phoneNumber,

  billingFullName: '',
  billingCompanyName: '',
  billingAddress: {
    line1: '',
    line2: '',
    line3: '',
    city: '',
    state: '',
    postalCode: '',
    countryCode: 'US',
  },

  shippingAddress: {
    line1: '',
    line2: '',
    line3: '',
    city: '',
    state: '',
    postalCode: '',
    countryCode: 'US',
  },

  paymentMethodId: '',
  autoPayAccepted: true,
  termsAccepted: false,

  title: 'Billing Manager',
  contactType: 'Billing Manager',
});

type WwexRegistrationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (data: WwexFormData, sellerFreightProviderId: number | null) => void;
  onManageClick?: () => Promise<boolean>;
};

export const WwexRegistrationModal = ({ isOpen, onClose, onSuccess, onManageClick }: WwexRegistrationModalProps) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const trackCarrierConnectionStep = useTrackWorkflow(Workflows.CARRIER_CONNECTION_FLOW);
  const { paymentMethodDetails } = usePaymentAndSubscriptionMegaContext();
  const successNotificationIdRef = useRef<string | null>(null);
  const paymentMethodSourceRef = useRef<string>(CarrierConnectionPaymentMethodSource.PRESELECTED);
  const addPaymentAttemptsRef = useRef(0);
  const modalHeaderId = useMemo(() => GuidService.newGuid(), []);
  const sellerCurrencyCode = useSelector(Global.selectSellerDefaultCurrency);
  const sellerCountry = useSelector(Global.selectSellerHomeCountry);
  const sellerFullName = useSelector(Global.selectSellerFullName);
  const sellerCompanyName = useSelector(Global.selectSellerCompanyName);
  const sellerEmail = useSelector(Global.selectSellerEmail);
  const sellerPhoneNumber = useSelector(Global.selectSellerPhoneNumber);

  const [view, setView] = useState<WwexModalView>('summary');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAllErrors, setShowAllErrors] = useState(false);
  const fsuiBillingInfoRef = useRef<BillingInfoData | undefined>(undefined);

  const [formData, setFormData] = useState<Partial<WwexFormData>>(() =>
    buildInitialFormData(sellerFullName ?? '', sellerCompanyName ?? '', sellerEmail ?? '', sellerPhoneNumber ?? ''),
  );

  // Reset the modal each time it opens, seeded from the seller's own profile
  useEffect(() => {
    if (isOpen) {
      setView('summary');
      setFormData(buildInitialFormData(sellerFullName ?? '', sellerCompanyName ?? '', sellerEmail ?? '', sellerPhoneNumber ?? ''));
      trackCarrierConnectionStep(WorkflowSteps.CARRIER_CONNECTION_MODAL_OPENED);
    } else {
      setShowAllErrors(false);
      setIsSubmitting(false);
      fsuiBillingInfoRef.current = undefined;
      paymentMethodSourceRef.current = CarrierConnectionPaymentMethodSource.PRESELECTED;
      addPaymentAttemptsRef.current = 0;
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- only re-seed when the modal opens/closes, not on every seller-selector change
  }, [isOpen]);

  const shouldShowBillingAddress = !!formData.paymentMethodId;
  const schema = useMemo(
    () => buildWwexFormSchema(sellerCountry ?? US, shouldShowBillingAddress),
    [sellerCountry, shouldShowBillingAddress],
  );
  const { errors, isValid } = useYupFormValidation({ form: formData, schema });
  const canActivate = isValid;

  const handleFormChange = useCallback((data: Partial<WwexFormData>) => {
    setFormData(data);
  }, []);

  const handleFsuiBillingInfoUpdate = useCallback((billingInfo?: BillingInfoData) => {
    fsuiBillingInfoRef.current = billingInfo;
  }, []);

  const handleManageAccountClick = useCallback(async () => {
    if (onManageClick) {
      const didSave = await onManageClick();
      if (!didSave) return;
    }
    navigate(getFreightProvidersTabPath());
    if (successNotificationIdRef.current) {
      NotificationService.removeNotification(successNotificationIdRef.current);
    }
  }, [onManageClick, navigate]);

  const submitRegistration = useCallback(
    async (dataToSubmit: Partial<WwexFormData>) => {
      setIsSubmitting(true);

      // Shipping address always mirrors billing address
      const finalDataToSubmit = { ...dataToSubmit, shippingAddress: dataToSubmit.billingAddress };

      try {
        const response = await openWwexAccount(finalDataToSubmit as WwexFormData);

        if (response.isSuccess) {
          trackCarrierConnectionStep(WorkflowSteps.COMPLETE);
          const notificationId = NotificationService.showNotification({
            content: onManageClick ? (
              <FlexColumn className={style.successNotificationContent} gap={Gap.Space8}>
                <Text textType={TextType.Paragraph} value={messages.successNotification} />
                <Button className={style.manageLinkButton} onClick={handleManageAccountClick} theme={THEME_CONSTANTS.link}>
                  <Text className={style.manageLinkText} textType={TextType.Paragraph} value={messages.manageAccountLink} />
                  <FontAwesomeIcon className={style.manageLinkIcon} icon={faArrowRight} />
                </Button>
              </FlexColumn>
            ) : (
              <Text textType={TextType.Paragraph} value={messages.successNotification} />
            ),
            dontHide: true,
            notificationType: 'success',
          });
          successNotificationIdRef.current = notificationId;

          onSuccess?.(dataToSubmit as WwexFormData, response.sellerFreightProviderId);
          onClose();
        } else {
          const errorMessages = response.errors?.map((e) => e.message).filter(Boolean) || [];
          const errorMessage =
            errorMessages.length > 0 ? errorMessages.join(', ') : response.message || intl.formatMessage(messages.unexpectedErrorOccurred);

          trackCarrierConnectionStep(WorkflowSteps.ERROR, {
            error_source: CarrierConnectionErrorSource.API_ERROR,
            error_code: response.errors?.[0]?.code ?? 'UNKNOWN_ERROR',
            error_message: errorMessage,
          });

          NotificationService.showNotification({
            content: intl.formatMessage(messages.registrationFailedWithMessage, { errorMessage }),
            notificationType: 'error',
          });
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : intl.formatMessage(messages.unexpectedErrorOccurred);

        trackCarrierConnectionStep(WorkflowSteps.ERROR, {
          error_source: CarrierConnectionErrorSource.UNEXPECTED_ERROR,
          error_message: errorMessage,
        });

        NotificationService.showNotification({
          content: intl.formatMessage(messages.registrationFailedWithSupport, { errorMessage }),
          notificationType: 'error',
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [intl, onClose, onSuccess, onManageClick, handleManageAccountClick, trackCarrierConnectionStep],
  );

  const handleActivate = useCallback(async () => {
    setShowAllErrors(true);

    if (!isValid) {
      trackCarrierConnectionStep(WorkflowSteps.ERROR, {
        error_source: CarrierConnectionErrorSource.CLIENT_VALIDATION,
        invalid_fields: Object.keys(errors),
      });
      NotificationService.showNotification({
        content: intl.formatMessage(messages.errorNotification),
        notificationType: 'error',
      });
      return;
    }

    trackCarrierConnectionStep(WorkflowSteps.CARRIER_CONNECTION_SUBMITTED, {
      payment_method_source: paymentMethodSourceRef.current,
      existing_payment_method_count: paymentMethodDetails.length,
      add_payment_attempts: addPaymentAttemptsRef.current,
    });
    await submitRegistration(formData);
  }, [isValid, errors, formData, intl, submitRegistration, trackCarrierConnectionStep, paymentMethodDetails.length]);

  const handleCancel = useCallback(() => {
    trackCarrierConnectionStep(WorkflowSteps.CANCEL);
    onClose();
  }, [onClose, trackCarrierConnectionStep]);

  const handleBackToSummary = useCallback(() => {
    setView('summary');
  }, []);

  const handleEditAccountInfo = useCallback(() => setView('editAccountInfo'), []);
  const handleEditBillingAddress = useCallback(() => setView('editBillingAddress'), []);
  const handleAddPaymentMethod = useCallback(() => {
    addPaymentAttemptsRef.current += 1;
    setView('addPaymentMethod');
  }, []);

  const handlePaymentMethodSourceChange = useCallback((source: string) => {
    paymentMethodSourceRef.current = source;
  }, []);

  const handleSaveSubView = useCallback((data: Partial<WwexFormData>) => {
    setFormData(data);
    setView('summary');
  }, []);

  const handlePaymentMethodCreated = useCallback(
    (paymentMethodId: string, billingInfo?: BillingInfoData) => {
      paymentMethodSourceRef.current = CarrierConnectionPaymentMethodSource.NEWLY_ADDED;
      trackCarrierConnectionStep(WorkflowSteps.CARRIER_CONNECTION_PAYMENT_METHOD_SELECTED);
      trackCarrierConnectionStep(WorkflowSteps.INTERACTION, {
        section: CarrierConnectionInteractionSection.PAYMENT,
        action: CarrierConnectionInteractionAction.ADD_NEW_SAVED,
      });

      const billingInfoToUse = billingInfo || fsuiBillingInfoRef.current;

      if (!billingInfoToUse) {
        setFormData((prev) => ({ ...prev, paymentMethodId }));
        setView('summary');
        return;
      }

      const billingAddress: Address = {
        line1: billingInfoToUse[BillingInfoFieldName.Line1] || '',
        line2: billingInfoToUse[BillingInfoFieldName.Line2] || '',
        line3: '',
        city: billingInfoToUse[BillingInfoFieldName.City] || '',
        state: billingInfoToUse[BillingInfoFieldName.State] || '',
        postalCode: billingInfoToUse[BillingInfoFieldName.PostalCode] || '',
        // Always the seller's own home country, not whatever the Adyen billing form captured
        countryCode: sellerCountry ?? US,
      };

      setFormData((prev) => ({
        ...prev,
        paymentMethodId,
        billingFullName: billingInfoToUse[BillingInfoFieldName.FullName] || '',
        billingCompanyName: billingInfoToUse[BillingInfoFieldName.Company] || '',
        billingAddress,
      }));
      setView('summary');
    },
    [sellerCountry, trackCarrierConnectionStep],
  );

  if (!isOpen) {
    return null;
  }

  return (
    <ModalPortal onClose={handleCancel}>
      <FsuiContextProvider currencyCode={sellerCurrencyCode ?? 'USD'} transactionCategory={TransactionCategory.Freight}>
        <FinancialServicesUIProviderWrapper onBillingInfoUpdate={handleFsuiBillingInfoUpdate}>
          <ErrorBoundary errorMessageId='speedShipRegistrationModal' onError={handleCancel}>
            <Modal className={style.modal}>
              {view === 'summary' && (
                <>
                  <ModalHeader className={style.modalHeader} closeModal={handleCancel} id={modalHeaderId}>
                    {intl.formatMessage(messages.modalTitle)}
                  </ModalHeader>

                  <ModalBody className={style.modalBody}>
                    <FlexColumn gap={Gap.Space16}>
                      <Text textType={TextType.Paragraph} value={messages.modalSubtitle} />
                      <WwexSummaryView
                        errors={errors as Record<string, ValidationError>}
                        formData={formData}
                        isDisabled={isSubmitting}
                        onAddPaymentMethod={handleAddPaymentMethod}
                        onChange={handleFormChange}
                        onEditAccountInfo={handleEditAccountInfo}
                        onEditBillingAddress={handleEditBillingAddress}
                        onPaymentMethodSourceChange={handlePaymentMethodSourceChange}
                        showAllErrors={showAllErrors}
                      />
                    </FlexColumn>
                  </ModalBody>

                  <ModalFooter>
                    <Button
                      ariaLabel={intl.formatMessage(messages.cancelButton)}
                      disabled={isSubmitting}
                      onClick={handleCancel}
                      theme={THEME_CONSTANTS.white}
                    >
                      {intl.formatMessage(messages.cancelButton)}
                    </Button>
                    <Button
                      ariaLabel={intl.formatMessage(messages.submitButton)}
                      disabled={isSubmitting || !canActivate}
                      onClick={handleActivate}
                      theme={THEME_CONSTANTS.primary}
                    >
                      {intl.formatMessage(messages.submitButton)}
                    </Button>
                  </ModalFooter>
                </>
              )}

              {view === 'editAccountInfo' && (
                <EditAccountInfoView
                  formData={formData}
                  headerId={modalHeaderId}
                  isDisabled={isSubmitting}
                  onBack={handleBackToSummary}
                  onCancel={handleBackToSummary}
                  onSave={handleSaveSubView}
                  sellerCountry={sellerCountry ?? undefined}
                />
              )}

              {view === 'addPaymentMethod' && (
                <AddPaymentMethodView
                  headerId={modalHeaderId}
                  isDisabled={isSubmitting}
                  onBack={handleBackToSummary}
                  onCancel={handleBackToSummary}
                  onPaymentMethodCreated={handlePaymentMethodCreated}
                />
              )}

              {view === 'editBillingAddress' && (
                <EditBillingAddressView
                  formData={formData}
                  headerId={modalHeaderId}
                  isDisabled={isSubmitting}
                  onBack={handleBackToSummary}
                  onCancel={handleBackToSummary}
                  onSave={handleSaveSubView}
                />
              )}
            </Modal>
          </ErrorBoundary>
        </FinancialServicesUIProviderWrapper>
      </FsuiContextProvider>
    </ModalPortal>
  );
};

// Re-export type for backward compatibility
export type { WwexFormData };
